import React, { useState, useMemo } from "react";
import useAdminApi from "@/hooks/useAdminApi";

/**
 * CATALOG-2B-SUPPLIER-LINEAGE (Jun 2026) — Supplier Layer audit (read-only).
 *
 * Tab inside the CATALOG-2B Import Review page. Surfaces:
 *   • current Supplier Layer snapshot (entities / relationships counts,
 *     by entity_type / relationship_type / status, deferred summary
 *     from the latest SUPPLIER-3 run);
 *   • paginated list of `catalog_audit_log` entries scoped to
 *     `action: catalog_supplier_*` (SUPPLIER-2 schema apply +
 *     SUPPLIER-3 relationships apply);
 *   • drill-down detail with the FULL `entities_applied` /
 *     `relationships_applied` / `deferred_entities` /
 *     `deferred_relationships` lists.
 *
 * Strict invariants (per GO 2026-06-14 CATALOG-2B-SUPPLIER-LINEAGE):
 *   • Read-only. No POST / PATCH / DELETE.
 *   • No marketplace activation. No supplier directory. No external
 *     roles. Apply / rollback NOT exposed here.
 */
const SupplierLineagePanel = () => {
  const [selectedLogId, setSelectedLogId] = useState(null);
  const [subTab, setSubTab] = useState("runs");

  const { data: snapshot, loading: snapshotLoading, error: snapshotError } =
    useAdminApi("/admin/catalog/supplier-snapshot");
  const { data: runs, loading: runsLoading, error: runsError } = useAdminApi(
    subTab === "runs" ? "/admin/catalog/supplier-runs" : null,
    { params: { limit: 50 } },
  );
  const { data: detail, loading: detailLoading } = useAdminApi(
    selectedLogId ? `/admin/catalog/supplier-runs/${selectedLogId}` : null,
  );

  const items = runs?.items || [];

  return (
    <section
      data-testid="admin-supplier-lineage"
      className="space-y-4"
    >
      <p
        data-testid="supplier-lineage-policy-hint"
        className="rounded-md border border-emerald-200 bg-emerald-50 p-2 text-[11px] text-emerald-800"
      >
        Read-only audit of the Supplier Layer. Marketplace activation, public
        supplier directory, paid access and customer-facing surfaces remain
        HARD BLOCKED until explicit nominal GO.
      </p>

      {/* Snapshot ----------------------------------------------------------- */}
      <div
        data-testid="supplier-lineage-snapshot"
        className="grid gap-3 md:grid-cols-2"
      >
        <SnapshotCard
          title="Supplier entities"
          testId="supplier-lineage-entities-card"
          loading={snapshotLoading}
          error={snapshotError}
          total={snapshot?.entities?.total}
          byMain={snapshot?.entities?.by_entity_type}
          byMainField="entity_type"
          byStatus={snapshot?.entities?.by_status}
        />
        <SnapshotCard
          title="Brand relationships"
          testId="supplier-lineage-relationships-card"
          loading={snapshotLoading}
          error={snapshotError}
          total={snapshot?.relationships?.total}
          byMain={snapshot?.relationships?.by_relationship_type}
          byMainField="relationship_type"
          byStatus={snapshot?.relationships?.by_status}
        />
      </div>

      <DeferredSummary deferred={snapshot?.latest_relationships_run_deferred} />

      {/* Sub-tab switcher (Runs | Entities) -------------------------------- */}
      <nav
        data-testid="supplier-lineage-subtabs"
        className="flex gap-1 border-b border-slate-200"
      >
        <SubTabButton
          testId="supplier-lineage-subtab-runs"
          active={subTab === "runs"}
          onClick={() => setSubTab("runs")}
          label="Runs"
        />
        <SubTabButton
          testId="supplier-lineage-subtab-entities"
          active={subTab === "entities"}
          onClick={() => setSubTab("entities")}
          label="Entities"
        />
        <SubTabButton
          testId="supplier-lineage-subtab-relationships"
          active={subTab === "relationships"}
          onClick={() => setSubTab("relationships")}
          label="Relationships"
        />
      </nav>

      {subTab === "entities" ? <SupplierEntitiesBrowser /> : null}
      {subTab === "relationships" ? <SupplierRelationshipsBrowser /> : null}

      {subTab === "runs" ? (
        <RunsList
          loading={runsLoading}
          error={runsError}
          total={runs?.total}
          items={items}
          onOpen={setSelectedLogId}
        />
      ) : null}

      {/* Drill-down detail panel (runs only) ------------------------------ */}
      {subTab === "runs" && selectedLogId ? (
        <RunDetailPanel
          logId={selectedLogId}
          detail={detail}
          loading={detailLoading}
          onClose={() => setSelectedLogId(null)}
        />
      ) : null}
    </section>
  );
};

const SubTabButton = ({ testId, active, onClick, label }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={onClick}
    className={`px-3 py-1.5 text-xs font-medium ${
      active
        ? "border-b-2 border-emerald-600 text-emerald-700"
        : "text-slate-500 hover:text-slate-700"
    }`}
  >
    {label}
  </button>
);

const RunsList = ({ loading, error, total, items, onOpen }) => (
  <div className="overflow-hidden rounded-md border border-slate-200">
    <header className="flex items-center justify-between bg-slate-50 px-3 py-2">
      <h3 className="text-xs font-semibold text-slate-800">
        Supplier audit runs
      </h3>
      <span className="text-[10px] text-slate-500">
        {loading ? "Loading…" : `${total ?? 0} run(s)`}
      </span>
    </header>
    {error ? (
      <p
        data-testid="supplier-lineage-runs-error"
        className="px-3 py-2 text-xs text-rose-700"
      >
        {String(error?.detail || error?.message || error)}
      </p>
    ) : null}
    <table
      data-testid="supplier-lineage-runs-table"
      className="w-full text-left text-xs"
    >
      <thead className="bg-slate-100 text-[10px] uppercase tracking-wide text-slate-600">
        <tr>
          <th className="px-2 py-1.5">When</th>
          <th className="px-2 py-1.5">Action</th>
          <th className="px-2 py-1.5">Actor</th>
          <th className="px-2 py-1.5">Scope</th>
          <th className="px-2 py-1.5">Summary</th>
          <th className="px-2 py-1.5">&nbsp;</th>
        </tr>
      </thead>
      <tbody className="text-slate-700">
        {items.length === 0 && !loading ? (
          <tr>
            <td
              colSpan={6}
              className="px-2 py-4 text-center text-slate-400"
            >
              No supplier runs recorded yet.
            </td>
          </tr>
        ) : (
          items.map((it) => (
            <tr
              key={it.log_id}
              data-testid={`supplier-lineage-run-row-${it.log_id}`}
              className="border-t border-slate-100 hover:bg-slate-50"
            >
              <td className="px-2 py-1.5 font-mono text-[10px] text-slate-500">
                {formatAt(it.at)}
              </td>
              <td className="px-2 py-1.5">
                <ActionBadge action={it.action} />
              </td>
              <td className="px-2 py-1.5 text-[10px]">{it.actor || "—"}</td>
              <td className="px-2 py-1.5 text-[10px]">
                {(it.scope || []).join(", ") || "—"}
              </td>
              <td className="px-2 py-1.5 text-[10px]">
                <SummaryChips summary={it.summary} />
              </td>
              <td className="px-2 py-1.5">
                <button
                  type="button"
                  data-testid={`supplier-lineage-open-detail-${it.log_id}`}
                  className="rounded border border-slate-200 px-2 py-0.5 text-[10px] hover:bg-slate-100"
                  onClick={() => onOpen(it.log_id)}
                >
                  View
                </button>
              </td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>
);

// ---------------------------------------------------------------------------
// CATALOG-SUPPLIER-ENTITIES-BROWSE (Jun 2026) — paginated entities table.
// ---------------------------------------------------------------------------

const SupplierEntitiesBrowser = () => {
  const [entityType, setEntityType] = useState("");
  const [status, setStatus] = useState("");
  const [country, setCountry] = useState("");
  const [needsReview, setNeedsReview] = useState("");
  const [brandId, setBrandId] = useState("");
  const [q, setQ] = useState("");
  const [offset, setOffset] = useState(0);
  const [openRow, setOpenRow] = useState(null);

  const params = useMemo(() => {
    const p = { limit: 50, offset };
    if (entityType) p.entity_type = entityType;
    if (status) p.status = status;
    if (country) p.country = country;
    if (needsReview) p.needs_human_review = needsReview === "yes";
    if (brandId) p.brand_id = brandId;
    if (q.trim()) p.q = q.trim();
    return p;
  }, [entityType, status, country, needsReview, brandId, q, offset]);

  const { data, loading, error } = useAdminApi(
    "/admin/catalog/supplier-entities",
    { params },
  );
  const items = data?.items || [];

  return (
    <div
      data-testid="supplier-entities-browser"
      className="space-y-2 rounded-md border border-slate-200"
    >
      <header className="flex flex-wrap items-center gap-2 bg-slate-50 px-3 py-2">
        <h3 className="text-xs font-semibold text-slate-800">
          Supplier entities
        </h3>
        <span
          data-testid="supplier-entities-total"
          className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-800"
        >
          {loading ? "…" : `${data?.total ?? 0} entity(ies)`}
        </span>
        <span className="ml-auto text-[10px] text-slate-500">
          Read-only · draft visible · no marketplace activation
        </span>
      </header>

      <div className="grid grid-cols-2 gap-2 px-3 md:grid-cols-6">
        <Select
          testId="supplier-entities-filter-type"
          value={entityType}
          onChange={(v) => {
            setEntityType(v);
            setOffset(0);
          }}
          label="entity_type"
          options={[
            "", "manufacturer", "distributor", "brand_owner",
            "parent_company", "operating_company", "licensor", "licensee",
            "operator", "retailer_partner", "marketplace_supplier",
            "oem_manufacturer", "unknown",
          ]}
        />
        <Select
          testId="supplier-entities-filter-status"
          value={status}
          onChange={(v) => {
            setStatus(v);
            setOffset(0);
          }}
          label="status"
          options={["", "draft", "active", "inactive", "deprecated", "review"]}
        />
        <Input
          testId="supplier-entities-filter-country"
          value={country}
          onChange={(v) => {
            setCountry(v);
            setOffset(0);
          }}
          placeholder="country"
        />
        <Select
          testId="supplier-entities-filter-review"
          value={needsReview}
          onChange={(v) => {
            setNeedsReview(v);
            setOffset(0);
          }}
          label="needs_human_review"
          options={["", "yes", "no"]}
        />
        <Input
          testId="supplier-entities-filter-brand-id"
          value={brandId}
          onChange={(v) => {
            setBrandId(v);
            setOffset(0);
          }}
          placeholder="brand_id (BRD-…)"
        />
        <Input
          testId="supplier-entities-filter-q"
          value={q}
          onChange={(v) => {
            setQ(v);
            setOffset(0);
          }}
          placeholder="search id/name/slug"
        />
      </div>

      {error ? (
        <p
          data-testid="supplier-entities-error"
          className="px-3 py-2 text-xs text-rose-700"
        >
          {String(error?.detail || error?.message || error)}
        </p>
      ) : null}

      <table
        data-testid="supplier-entities-table"
        className="w-full text-left text-xs"
      >
        <thead className="bg-slate-100 text-[10px] uppercase tracking-wide text-slate-600">
          <tr>
            <th className="px-2 py-1.5">internal_id</th>
            <th className="px-2 py-1.5">name</th>
            <th className="px-2 py-1.5">type</th>
            <th className="px-2 py-1.5">country</th>
            <th className="px-2 py-1.5">status</th>
            <th className="px-2 py-1.5">linked brand(s)</th>
            <th className="px-2 py-1.5">review</th>
            <th className="px-2 py-1.5">conf</th>
            <th className="px-2 py-1.5">src</th>
            <th className="px-2 py-1.5">&nbsp;</th>
          </tr>
        </thead>
        <tbody className="text-slate-700">
          {items.length === 0 && !loading ? (
            <tr>
              <td colSpan={10} className="px-2 py-4 text-center text-slate-400">
                No supplier entities match the current filters.
              </td>
            </tr>
          ) : (
            items.map((e) => {
              const linked = [
                e.linked_brand_id,
                ...(e.linked_via_relationships || []).filter(
                  (b) => b !== e.linked_brand_id,
                ),
              ].filter(Boolean);
              const isOpen = openRow === e.internal_id;
              return (
                <React.Fragment key={e.internal_id}>
                  <tr
                    data-testid={`supplier-entity-row-${e.internal_id}`}
                    className="border-t border-slate-100 hover:bg-slate-50"
                  >
                    <td className="px-2 py-1.5 font-mono text-[10px]">
                      {e.internal_id}
                    </td>
                    <td className="px-2 py-1.5">{e.name}</td>
                    <td className="px-2 py-1.5 text-[10px]">{e.entity_type}</td>
                    <td className="px-2 py-1.5 text-[10px]">
                      {e.country || "—"}
                    </td>
                    <td className="px-2 py-1.5">
                      <span className="inline-block rounded bg-slate-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-slate-700">
                        {e.status}
                      </span>
                    </td>
                    <td className="px-2 py-1.5 font-mono text-[10px]">
                      {linked.length ? linked.join(", ") : "—"}
                    </td>
                    <td className="px-2 py-1.5">
                      {e.needs_human_review ? (
                        <span className="inline-block rounded bg-amber-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-amber-800">
                          review
                        </span>
                      ) : (
                        <span className="text-[10px] text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-[10px]">
                      {e.confidence || "—"}
                    </td>
                    <td className="px-2 py-1.5 text-[10px]">
                      {e.source_urls_count}
                    </td>
                    <td className="px-2 py-1.5">
                      <button
                        type="button"
                        data-testid={`supplier-entity-toggle-${e.internal_id}`}
                        className="rounded border border-slate-200 px-2 py-0.5 text-[10px] hover:bg-slate-100"
                        onClick={() => setOpenRow(isOpen ? null : e.internal_id)}
                      >
                        {isOpen ? "Hide" : "Detail"}
                      </button>
                    </td>
                  </tr>
                  {isOpen ? (
                    <tr
                      data-testid={`supplier-entity-detail-${e.internal_id}`}
                      className="bg-slate-50/50"
                    >
                      <td colSpan={10} className="px-3 py-2 text-[10px]">
                        <dl className="grid grid-cols-2 gap-x-4 gap-y-1 md:grid-cols-4">
                          <dt className="font-semibold">slug</dt>
                          <dd className="font-mono">{e.slug || "—"}</dd>
                          <dt className="font-semibold">region_scope</dt>
                          <dd>{e.region_scope || "—"}</dd>
                          <dt className="font-semibold">website</dt>
                          <dd>{e.website || "—"}</dd>
                          <dt className="font-semibold">updated_at</dt>
                          <dd className="font-mono">{e.updated_at}</dd>
                        </dl>
                        {e.notes_preview ? (
                          <p className="mt-2 italic text-slate-600">
                            “{e.notes_preview}”
                          </p>
                        ) : null}
                      </td>
                    </tr>
                  ) : null}
                </React.Fragment>
              );
            })
          )}
        </tbody>
      </table>

      <div className="flex items-center justify-between px-3 pb-3 text-[10px] text-slate-500">
        <span>
          Showing {items.length} of {data?.total ?? 0}
        </span>
        <div className="flex gap-1">
          <button
            type="button"
            data-testid="supplier-entities-prev"
            disabled={offset === 0}
            className="rounded border border-slate-200 px-2 py-0.5 disabled:opacity-50"
            onClick={() => setOffset(Math.max(0, offset - 50))}
          >
            Prev
          </button>
          <button
            type="button"
            data-testid="supplier-entities-next"
            disabled={offset + items.length >= (data?.total ?? 0)}
            className="rounded border border-slate-200 px-2 py-0.5 disabled:opacity-50"
            onClick={() => setOffset(offset + 50)}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

const Select = ({ testId, value, onChange, label, options }) => (
  <select
    data-testid={testId}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className="rounded border border-slate-200 bg-white px-1.5 py-1 text-[10px]"
  >
    {options.map((opt) => (
      <option key={opt} value={opt}>
        {opt === "" ? `(any ${label})` : opt}
      </option>
    ))}
  </select>
);

const Input = ({ testId, value, onChange, placeholder }) => (
  <input
    type="text"
    data-testid={testId}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    className="rounded border border-slate-200 bg-white px-1.5 py-1 text-[10px]"
  />
);

// ---------------------------------------------------------------------------
// CATALOG-SUPPLIER-RELATIONSHIPS-BROWSE (Jun 2026)
// ---------------------------------------------------------------------------

const SupplierRelationshipsBrowser = () => {
  const [brandId, setBrandId] = useState("");
  const [supplierId, setSupplierId] = useState("");
  const [relType, setRelType] = useState("");
  const [territory, setTerritory] = useState("");
  const [status, setStatus] = useState("");
  const [needsReview, setNeedsReview] = useState("");
  const [q, setQ] = useState("");
  const [offset, setOffset] = useState(0);
  const [openRow, setOpenRow] = useState(null);

  const params = useMemo(() => {
    const p = { limit: 50, offset };
    if (brandId) p.brand_internal_id = brandId;
    if (supplierId) p.supplier_internal_id = supplierId;
    if (relType) p.relationship_type = relType;
    if (territory) p.territory = territory;
    if (status) p.status = status;
    if (needsReview) p.needs_human_review = needsReview === "yes";
    if (q.trim()) p.q = q.trim();
    return p;
  }, [brandId, supplierId, relType, territory, status, needsReview, q, offset]);

  const { data, loading, error } = useAdminApi(
    "/admin/catalog/supplier-relationships",
    { params },
  );
  const items = data?.items || [];

  return (
    <div
      data-testid="supplier-relationships-browser"
      className="space-y-2 rounded-md border border-slate-200"
    >
      <header className="flex flex-wrap items-center gap-2 bg-slate-50 px-3 py-2">
        <h3 className="text-xs font-semibold text-slate-800">
          Brand relationships
        </h3>
        <span
          data-testid="supplier-relationships-total"
          className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-800"
        >
          {loading ? "…" : `${data?.total ?? 0} relationship(s)`}
        </span>
        <span className="ml-auto text-[10px] text-slate-500">
          Read-only · draft visible · no marketplace/supplier activation
        </span>
      </header>

      <div className="grid grid-cols-2 gap-2 px-3 md:grid-cols-7">
        <Input
          testId="supplier-relationships-filter-brand-id"
          value={brandId}
          onChange={(v) => {
            setBrandId(v);
            setOffset(0);
          }}
          placeholder="brand_internal_id"
        />
        <Input
          testId="supplier-relationships-filter-supplier-id"
          value={supplierId}
          onChange={(v) => {
            setSupplierId(v);
            setOffset(0);
          }}
          placeholder="supplier_internal_id"
        />
        <Select
          testId="supplier-relationships-filter-type"
          value={relType}
          onChange={(v) => {
            setRelType(v);
            setOffset(0);
          }}
          label="relationship_type"
          options={[
            "", "brand_owner", "parent_company", "distributor", "licensee",
            "licensor", "manufacturer", "regional_distributor",
            "supplier_candidate", "shared_parent", "license_model",
            "oem_manufacturing", "brand_heritage_vs_legal_entity",
            "unresolved",
          ]}
        />
        <Input
          testId="supplier-relationships-filter-territory"
          value={territory}
          onChange={(v) => {
            setTerritory(v);
            setOffset(0);
          }}
          placeholder="territory"
        />
        <Select
          testId="supplier-relationships-filter-status"
          value={status}
          onChange={(v) => {
            setStatus(v);
            setOffset(0);
          }}
          label="status"
          options={["", "draft", "active", "inactive", "deprecated", "review"]}
        />
        <Select
          testId="supplier-relationships-filter-review"
          value={needsReview}
          onChange={(v) => {
            setNeedsReview(v);
            setOffset(0);
          }}
          label="needs_human_review"
          options={["", "yes", "no"]}
        />
        <Input
          testId="supplier-relationships-filter-q"
          value={q}
          onChange={(v) => {
            setQ(v);
            setOffset(0);
          }}
          placeholder="search id/notes"
        />
      </div>

      {error ? (
        <p
          data-testid="supplier-relationships-error"
          className="px-3 py-2 text-xs text-rose-700"
        >
          {String(error?.detail || error?.message || error)}
        </p>
      ) : null}

      <table
        data-testid="supplier-relationships-table"
        className="w-full text-left text-xs"
      >
        <thead className="bg-slate-100 text-[10px] uppercase tracking-wide text-slate-600">
          <tr>
            <th className="px-2 py-1.5">internal_id</th>
            <th className="px-2 py-1.5">brand</th>
            <th className="px-2 py-1.5">supplier</th>
            <th className="px-2 py-1.5">type</th>
            <th className="px-2 py-1.5">territory</th>
            <th className="px-2 py-1.5">status</th>
            <th className="px-2 py-1.5">review</th>
            <th className="px-2 py-1.5">conf</th>
            <th className="px-2 py-1.5">src</th>
            <th className="px-2 py-1.5">&nbsp;</th>
          </tr>
        </thead>
        <tbody className="text-slate-700">
          {items.length === 0 && !loading ? (
            <tr>
              <td colSpan={10} className="px-2 py-4 text-center text-slate-400">
                No relationships match the current filters.
              </td>
            </tr>
          ) : (
            items.map((r) => {
              const isOpen = openRow === r.internal_id;
              return (
                <React.Fragment key={r.internal_id}>
                  <tr
                    data-testid={`supplier-relationship-row-${r.internal_id}`}
                    className="border-t border-slate-100 hover:bg-slate-50"
                  >
                    <td className="px-2 py-1.5 font-mono text-[10px]">
                      {r.internal_id}
                    </td>
                    <td className="px-2 py-1.5 text-[10px]">
                      <span className="font-mono">{r.brand_internal_id}</span>
                      {r.brand_name ? (
                        <span className="ml-1 text-slate-500">
                          ({r.brand_name})
                        </span>
                      ) : null}
                    </td>
                    <td className="px-2 py-1.5 text-[10px]">
                      <span className="font-mono">{r.supplier_internal_id}</span>
                      {r.supplier_name ? (
                        <span className="ml-1 text-slate-500">
                          ({r.supplier_name})
                        </span>
                      ) : null}
                    </td>
                    <td className="px-2 py-1.5 text-[10px]">{r.relationship_type}</td>
                    <td className="px-2 py-1.5 font-mono text-[10px]">
                      {r.territory}
                    </td>
                    <td className="px-2 py-1.5">
                      <span className="inline-block rounded bg-slate-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-slate-700">
                        {r.status}
                      </span>
                    </td>
                    <td className="px-2 py-1.5">
                      {r.needs_human_review ? (
                        <span className="inline-block rounded bg-amber-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-amber-800">
                          review
                        </span>
                      ) : (
                        <span className="text-[10px] text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-[10px]">
                      {r.confidence || "—"}
                    </td>
                    <td className="px-2 py-1.5 text-[10px]">
                      {r.source_urls_count}
                    </td>
                    <td className="px-2 py-1.5">
                      <button
                        type="button"
                        data-testid={`supplier-relationship-toggle-${r.internal_id}`}
                        className="rounded border border-slate-200 px-2 py-0.5 text-[10px] hover:bg-slate-100"
                        onClick={() =>
                          setOpenRow(isOpen ? null : r.internal_id)
                        }
                      >
                        {isOpen ? "Hide" : "Detail"}
                      </button>
                    </td>
                  </tr>
                  {isOpen ? (
                    <tr
                      data-testid={`supplier-relationship-detail-${r.internal_id}`}
                      className="bg-slate-50/50"
                    >
                      <td colSpan={10} className="px-3 py-2 text-[10px]">
                        <dl className="grid grid-cols-2 gap-x-4 gap-y-1 md:grid-cols-4">
                          <dt className="font-semibold">source_rel_id</dt>
                          <dd className="font-mono">
                            {r.source_rel_id || "—"}
                          </dd>
                          <dt className="font-semibold">severity</dt>
                          <dd>{r.severity || "—"}</dd>
                          <dt className="font-semibold">created_at</dt>
                          <dd className="font-mono">{r.created_at}</dd>
                          <dt className="font-semibold">updated_at</dt>
                          <dd className="font-mono">{r.updated_at}</dd>
                        </dl>
                        {r.notes_preview ? (
                          <p className="mt-2 italic text-slate-600">
                            “{r.notes_preview}”
                          </p>
                        ) : null}
                      </td>
                    </tr>
                  ) : null}
                </React.Fragment>
              );
            })
          )}
        </tbody>
      </table>

      <div className="flex items-center justify-between px-3 pb-3 text-[10px] text-slate-500">
        <span>
          Showing {items.length} of {data?.total ?? 0}
        </span>
        <div className="flex gap-1">
          <button
            type="button"
            data-testid="supplier-relationships-prev"
            disabled={offset === 0}
            className="rounded border border-slate-200 px-2 py-0.5 disabled:opacity-50"
            onClick={() => setOffset(Math.max(0, offset - 50))}
          >
            Prev
          </button>
          <button
            type="button"
            data-testid="supplier-relationships-next"
            disabled={offset + items.length >= (data?.total ?? 0)}
            className="rounded border border-slate-200 px-2 py-0.5 disabled:opacity-50"
            onClick={() => setOffset(offset + 50)}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};


const formatAt = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return `${d.toISOString().slice(0, 19).replace("T", " ")}Z`;
  } catch {
    return iso;
  }
};

const ActionBadge = ({ action }) => {
  const map = {
    catalog_supplier_2_schema_apply: ["bg-sky-100 text-sky-800", "schema apply"],
    catalog_supplier_3_relationships_apply: [
      "bg-emerald-100 text-emerald-800",
      "relationships apply",
    ],
  };
  const [tone, label] = map[action] || ["bg-slate-100 text-slate-700", action];
  return (
    <span
      data-testid={`supplier-action-badge-${action}`}
      className={`inline-block rounded px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wide ${tone}`}
    >
      {label}
    </span>
  );
};

const SummaryChips = ({ summary }) => {
  if (!summary || Object.keys(summary).length === 0) {
    return <span className="text-slate-400">no-op</span>;
  }
  return (
    <span className="flex flex-wrap gap-1">
      {Object.entries(summary)
        .filter(([, v]) => typeof v === "number" && v > 0)
        .map(([k, v]) => (
          <span
            key={k}
            className="inline-block rounded bg-slate-50 px-1.5 py-0.5 font-mono text-[9px] text-slate-700 ring-1 ring-slate-200"
          >
            {k}={v}
          </span>
        ))}
    </span>
  );
};

const SnapshotCard = ({
  title,
  testId,
  loading,
  error,
  total,
  byMain,
  byMainField,
  byStatus,
}) => (
  <div
    data-testid={testId}
    className="rounded-md border border-slate-200 bg-white p-3"
  >
    <div className="flex items-baseline justify-between">
      <h4 className="text-xs font-semibold text-slate-800">{title}</h4>
      <span className="font-mono text-base text-slate-900">
        {loading ? "…" : total ?? "—"}
      </span>
    </div>
    {error ? (
      <p className="mt-2 text-[10px] text-rose-700">
        {String(error?.detail || error?.message || error)}
      </p>
    ) : null}
    <div className="mt-2 grid grid-cols-2 gap-2">
      <BreakdownList
        label={`by ${byMainField}`}
        entries={byMain}
        valueKey={byMainField}
      />
      <BreakdownList label="by status" entries={byStatus} valueKey="status" />
    </div>
  </div>
);

const BreakdownList = ({ label, entries, valueKey }) => (
  <div>
    <p className="text-[9px] font-semibold uppercase tracking-wide text-slate-500">
      {label}
    </p>
    <ul className="mt-1 space-y-0.5 text-[10px]">
      {(entries || []).map((e) => (
        <li key={String(e[valueKey])} className="flex justify-between">
          <span className="font-mono text-slate-700">{String(e[valueKey])}</span>
          <span className="font-mono text-slate-500">{e.count}</span>
        </li>
      ))}
      {(!entries || entries.length === 0) && (
        <li className="italic text-slate-400">—</li>
      )}
    </ul>
  </div>
);

const DeferredSummary = ({ deferred }) => {
  if (!deferred) return null;
  return (
    <div
      data-testid="supplier-lineage-deferred-summary"
      className="rounded-md border border-amber-200 bg-amber-50 p-3"
    >
      <p className="text-[10px] font-semibold uppercase tracking-wide text-amber-900">
        Hard gates from latest SUPPLIER-3 run
      </p>
      <p className="mt-1 text-[10px] text-amber-800">
        Log <span className="font-mono">{deferred.log_id?.slice(0, 8)}…</span>
        {" • "}{formatAt(deferred.at)}
      </p>
      <div className="mt-2 grid gap-2 md:grid-cols-2">
        <DeferList
          label="Deferred entities"
          testId="supplier-deferred-entities-list"
          tone="bg-white"
          entries={deferred.deferred_entities}
        />
        <DeferList
          label="Deferred relationships"
          testId="supplier-deferred-relationships-list"
          tone="bg-white"
          entries={deferred.deferred_relationships}
        />
      </div>
    </div>
  );
};

const DeferList = ({ label, testId, tone, entries }) => (
  <div
    data-testid={testId}
    className={`rounded border border-amber-100 p-2 ${tone}`}
  >
    <p className="text-[9px] font-semibold uppercase tracking-wide text-amber-800">
      {label} ({entries?.length ?? 0})
    </p>
    <ul className="mt-1 max-h-32 list-disc overflow-auto pl-4 text-[10px] font-mono text-amber-900">
      {(entries || []).map((e) => (
        <li key={String(e)}>{String(e)}</li>
      ))}
      {(!entries || entries.length === 0) && (
        <li className="italic text-amber-700">—</li>
      )}
    </ul>
  </div>
);

const RunDetailPanel = ({ logId, detail, loading, onClose }) => (
  <div
    data-testid={`supplier-lineage-detail-${logId}`}
    className="rounded-md border border-slate-300 bg-slate-50 p-3"
  >
    <header className="flex items-center justify-between">
      <h3 className="text-xs font-semibold text-slate-900">
        Run <span className="font-mono">{logId.slice(0, 8)}…</span>
      </h3>
      <button
        type="button"
        data-testid="supplier-lineage-close-detail"
        className="text-[11px] text-slate-500 underline hover:text-slate-700"
        onClick={onClose}
      >
        Close
      </button>
    </header>
    {loading ? (
      <p className="mt-2 text-[10px] text-slate-500">Loading…</p>
    ) : null}
    {detail ? (
      <div className="mt-3 space-y-3">
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-[10px] text-slate-700">
          <dt className="font-semibold">Action</dt>
          <dd className="font-mono">{detail.action}</dd>
          <dt className="font-semibold">Actor</dt>
          <dd>{detail.actor}</dd>
          <dt className="font-semibold">When</dt>
          <dd>{formatAt(detail.at)}</dd>
          <dt className="font-semibold">Job</dt>
          <dd className="font-mono">{detail.job_id || "—"}</dd>
          <dt className="font-semibold">Scope</dt>
          <dd>{(detail.scope || []).join(", ")}</dd>
        </dl>
        <ActionsGrid actions={detail.actions || {}} />
      </div>
    ) : null}
  </div>
);

const ActionsGrid = ({ actions }) => {
  const arrayEntries = Object.entries(actions).filter(([, v]) =>
    Array.isArray(v),
  );
  if (arrayEntries.length === 0) {
    return (
      <p className="text-[10px] italic text-slate-500">
        This run carried no array-shaped actions (likely a SUPPLIER-2 schema
        run — see the action label above).
      </p>
    );
  }
  return (
    <div className="grid gap-2 md:grid-cols-2">
      {arrayEntries.map(([k, v]) => (
        <ActionList key={k} label={k} entries={v} />
      ))}
    </div>
  );
};

const ActionList = ({ label, entries }) => {
  const tone =
    label.includes("deferred") || label.includes("noop")
      ? "bg-amber-50 border-amber-200 text-amber-900"
      : label.includes("applied")
        ? "bg-emerald-50 border-emerald-200 text-emerald-900"
        : "bg-slate-50 border-slate-200 text-slate-800";
  return (
    <div
      data-testid={`supplier-lineage-actionlist-${label}`}
      className={`rounded-md border p-2 ${tone}`}
    >
      <p className="text-[10px] font-semibold uppercase">
        {label} ({entries.length})
      </p>
      <ul className="mt-1 max-h-48 list-disc overflow-auto pl-4 font-mono text-[10px]">
        {entries.slice(0, 500).map((e, i) => (
          <li key={i}>
            {typeof e === "string" ? e : JSON.stringify(e)}
          </li>
        ))}
        {entries.length > 500 ? (
          <li className="italic">…and {entries.length - 500} more</li>
        ) : null}
      </ul>
    </div>
  );
};

export default SupplierLineagePanel;
